import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Pencil, RotateCcw, Trash2, Save, Ruler, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALETTE = ['#FF0000', '#FB923C', '#FACC15', '#22C55E', '#06B6D4', '#3B82F6', '#A855F7', '#EC4899'];

function pixelDistance(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function totalPixelDistance(points) {
  let d = 0;
  for (let i = 1; i < points.length; i++) d += pixelDistance(points[i - 1], points[i]);
  return d;
}

/**
 * Floor plan editor.
 *
 * Two modes:
 *   - "calibrate": click two points on the image, enter real-world meters
 *     between them, applies scale_meters_per_pixel to the FloorPlan doc.
 *   - "draw": click points to build a polyline. Distance shown live in meters
 *     once a scale exists. Save POSTs a CourseTrack with is_floor_plan=true.
 *
 * Props:
 *   eventId, floorPlan {id,image_url,natural_width,natural_height,scale_meters_per_pixel,...}
 *   onSaved(track), onCancel(), onPlanUpdated(updatedFloorPlan), existingTrack (optional)
 */
export default function FloorPlanEditor({
  eventId, floorPlan, onSaved, onCancel, onPlanUpdated, existingTrack = null,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });            // displayed size in CSS px
  const [naturalSize, setNaturalSize] = useState({
    w: floorPlan?.natural_width || 0,
    h: floorPlan?.natural_height || 0,
  });
  const [mode, setMode] = useState(existingTrack ? 'draw' : (floorPlan?.scale_meters_per_pixel ? 'draw' : 'calibrate'));
  // Calibration state — uses image-natural-pixel coords
  const [calibPoints, setCalibPoints] = useState(floorPlan?.calibration_points || []);
  const [calibMeters, setCalibMeters] = useState(
    floorPlan?.calibration_real_meters != null ? String(floorPlan.calibration_real_meters) : ''
  );
  const [scale, setScale] = useState(floorPlan?.scale_meters_per_pixel || null);
  const [savingCalib, setSavingCalib] = useState(false);

  // Drawing state — pixel coords in image-natural space
  const [waypoints, setWaypoints] = useState(existingTrack?.track_points || []);
  const [name, setName] = useState(existingTrack?.name || '');
  const [color, setColor] = useState(existingTrack?.color || PALETTE[0]);
  const [startLabel, setStartLabel] = useState(existingTrack?.start_label || 'Start');
  const [endLabel, setEndLabel] = useState(existingTrack?.end_label || 'Finish');
  const [isCircuit, setIsCircuit] = useState(!!existingTrack?.is_circuit);
  const [saving, setSaving] = useState(false);

  const distanceMeters = useMemo(() => {
    if (!scale) return null;
    return totalPixelDistance(waypoints) * scale;
  }, [waypoints, scale]);

  // Keep displayed image size in sync (for converting click coords back to natural pixels).
  const measureImage = () => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    setImgSize({ w: rect.width, h: rect.height });
    if (imgRef.current.naturalWidth && (!naturalSize.w || !naturalSize.h)) {
      setNaturalSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  };

  useEffect(() => {
    const onResize = () => measureImage();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onImageLoad = () => {
    measureImage();
    if (imgRef.current && (!naturalSize.w || !naturalSize.h)) {
      const nw = imgRef.current.naturalWidth;
      const nh = imgRef.current.naturalHeight;
      setNaturalSize({ w: nw, h: nh });
      // Persist natural size on the floor plan if it wasn't recorded yet.
      if (floorPlan?.id && (!floorPlan.natural_width || !floorPlan.natural_height)) {
        axios.patch(`${API}/floor-plans/${floorPlan.id}`, {
          natural_width: nw, natural_height: nh,
        }).then((res) => onPlanUpdated?.(res.data)).catch(() => {});
      }
    }
  };

  // Convert a DOM click event to natural-pixel coordinates of the image.
  const clickToNatural = (e) => {
    if (!imgRef.current) return null;
    const rect = imgRef.current.getBoundingClientRect();
    const xCss = e.clientX - rect.left;
    const yCss = e.clientY - rect.top;
    if (xCss < 0 || yCss < 0 || xCss > rect.width || yCss > rect.height) return null;
    const sx = (naturalSize.w || rect.width) / rect.width;
    const sy = (naturalSize.h || rect.height) / rect.height;
    return { x: xCss * sx, y: yCss * sy };
  };

  // Convert natural-pixel coordinate to displayed CSS px (for SVG overlay).
  const naturalToCss = (p) => {
    if (!naturalSize.w || !naturalSize.h || !imgSize.w || !imgSize.h) return p;
    return {
      x: (p.x / naturalSize.w) * imgSize.w,
      y: (p.y / naturalSize.h) * imgSize.h,
    };
  };

  const handleCanvasClick = (e) => {
    const p = clickToNatural(e);
    if (!p) return;
    if (mode === 'calibrate') {
      setCalibPoints((prev) => {
        if (prev.length >= 2) return [p];  // start over after 2 points
        return [...prev, p];
      });
    } else {
      setWaypoints((prev) => [...prev, p]);
    }
  };

  const undoWaypoint = () => setWaypoints((prev) => prev.slice(0, -1));
  const clearWaypoints = () => setWaypoints([]);
  const clearCalib = () => setCalibPoints([]);

  const applyCalibration = async () => {
    if (calibPoints.length < 2) {
      toast.error(t('floorPlan.calibrateNeedTwo'));
      return;
    }
    const meters = parseFloat(calibMeters);
    if (!meters || meters <= 0) {
      toast.error(t('floorPlan.calibrateInvalidMeters'));
      return;
    }
    setSavingCalib(true);
    try {
      const res = await axios.patch(`${API}/floor-plans/${floorPlan.id}`, {
        calibration_points: calibPoints,
        calibration_real_meters: meters,
        natural_width: naturalSize.w || undefined,
        natural_height: naturalSize.h || undefined,
      });
      const updated = res.data;
      setScale(updated.scale_meters_per_pixel || null);
      onPlanUpdated?.(updated);
      toast.success(t('floorPlan.calibrationSaved'));
      setMode('draw');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.errors.generic'));
    } finally {
      setSavingCalib(false);
    }
  };

  const onSave = async () => {
    if (waypoints.length < 2) {
      toast.error(t('floorPlan.noPoints'));
      return;
    }
    if (!name.trim()) {
      toast.error(t('floorPlan.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      const body = {
        event_id: eventId,
        event_day_id: floorPlan?.event_day_id || null,
        name: name.trim(),
        description: '',
        color,
        track_points: waypoints,
        start_label: startLabel,
        end_label: isCircuit ? startLabel : endLabel,
        is_circuit: isCircuit,
        snap_to_roads: false,
        distance_meters: distanceMeters || null,
        source: 'floor_plan',
        is_floor_plan: true,
        floor_plan_id: floorPlan.id,
        scale_meters_per_pixel: scale,
        image_natural_width: naturalSize.w || undefined,
        image_natural_height: naturalSize.h || undefined,
      };
      let res;
      if (existingTrack?.id) {
        res = await axios.put(`${API}/courses/${existingTrack.id}`, body);
      } else {
        res = await axios.post(`${API}/events/${eventId}/courses`, body);
      }
      toast.success(t('floorPlan.savedSuccess'));
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const cssWaypoints = waypoints.map(naturalToCss);
  const cssCalib = calibPoints.map(naturalToCss);

  // Live calibration preview.
  const liveCalibMeters = useMemo(() => {
    if (calibPoints.length < 2) return null;
    const m = parseFloat(calibMeters);
    if (!m || m <= 0) return null;
    const px = pixelDistance(calibPoints[0], calibPoints[1]);
    if (px <= 0) return null;
    return { mpp: m / px, pixels: px };
  }, [calibPoints, calibMeters]);

  return (
    <div className="absolute inset-0 z-30 bg-white flex flex-col">
      {/* Toolbar */}
      <div className="border-b border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <Pencil className="w-4 h-4 text-emerald-600" />
          {t('floorPlan.title')}
          {floorPlan?.name ? <span className="text-xs text-slate-500 font-normal">— {floorPlan.name}</span> : null}
        </div>

        <div className="flex items-center gap-2 px-2 py-1 rounded bg-slate-100">
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded ${mode === 'calibrate' ? 'bg-white shadow-sm font-semibold' : 'text-slate-600'}`}
            onClick={() => setMode('calibrate')}
          >
            <Ruler className="w-3 h-3 inline mr-1" />
            {t('floorPlan.calibrate')}
          </button>
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded ${mode === 'draw' ? 'bg-white shadow-sm font-semibold' : 'text-slate-600'}`}
            onClick={() => setMode('draw')}
            disabled={!scale}
            title={!scale ? t('floorPlan.scaleNotSet') : ''}
          >
            <Pencil className="w-3 h-3 inline mr-1" />
            {t('floorPlan.draw')}
          </button>
        </div>

        {mode === 'draw' && (
          <>
            <Button size="sm" variant="ghost" onClick={undoWaypoint} disabled={!waypoints.length}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t('floorPlan.undo')}
            </Button>
            <Button size="sm" variant="ghost" onClick={clearWaypoints} disabled={!waypoints.length}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('floorPlan.clear')}
            </Button>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {mode === 'draw' && (
            <span className="text-xs text-slate-500">
              {waypoints.length} pts
              {distanceMeters != null ? ` · ${distanceMeters.toFixed(1)} m` : ''}
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="w-3.5 h-3.5 mr-1" /> {t('floorPlan.cancel')}
          </Button>
          {mode === 'draw' && (
            <Button size="sm" onClick={onSave} disabled={saving || waypoints.length < 2}>
              <Save className="w-3.5 h-3.5 mr-1" /> {t('floorPlan.save')}
            </Button>
          )}
        </div>
      </div>

      {/* Settings strip — calibrate mode */}
      {mode === 'calibrate' && (
        <div className="border-b border-slate-200 bg-slate-50/60 p-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">{t('floorPlan.calibrateDistance')}</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={calibMeters}
                onChange={(e) => setCalibMeters(e.target.value)}
                placeholder={t('floorPlan.calibrateDistancePlaceholder')}
              />
              <Button size="sm" variant="ghost" onClick={clearCalib} disabled={!calibPoints.length}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" onClick={applyCalibration} disabled={savingCalib || calibPoints.length < 2 || !calibMeters}>
                {t('floorPlan.calibrateApply')}
              </Button>
            </div>
          </div>
          <div className="md:col-span-2 text-xs text-slate-600 flex items-center">
            {scale ? (
              <span className="text-emerald-700">
                {t('floorPlan.scaleSet')}: {(scale * 100).toFixed(2)} cm/px ({(1 / scale).toFixed(2)} px/m)
              </span>
            ) : liveCalibMeters ? (
              <span>
                {t('floorPlan.previewScale')}: {(liveCalibMeters.mpp * 100).toFixed(2)} cm/px
                ({liveCalibMeters.pixels.toFixed(1)} px = {parseFloat(calibMeters).toFixed(2)} m)
              </span>
            ) : (
              <span className="text-amber-700">{t('floorPlan.scaleNotSet')}</span>
            )}
          </div>
        </div>
      )}

      {/* Settings strip — draw mode */}
      {mode === 'draw' && (
        <div className="border-b border-slate-200 bg-slate-50/60 p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">{t('floorPlan.name')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('floorPlan.namePlaceholder')} />
          </div>
          <div>
            <Label className="text-xs">{t('floorPlan.color')}</Label>
            <div className="flex gap-1 flex-wrap items-center h-9">
              {PALETTE.map((c) => (
                <button
                  key={c} type="button"
                  className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-slate-900' : 'border-white shadow'}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('floorPlan.startLabel')}</Label>
            <Input value={startLabel} onChange={(e) => setStartLabel(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('floorPlan.endLabel')}</Label>
            <Input value={endLabel} onChange={(e) => setEndLabel(e.target.value)} disabled={isCircuit} />
          </div>
          <div className="flex items-center gap-2 mt-5">
            <Switch checked={isCircuit} onCheckedChange={setIsCircuit} />
            <Label className="text-xs cursor-pointer">{t('floorPlan.isCircuit')}</Label>
          </div>
        </div>
      )}

      {/* Hint */}
      <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 text-xs text-emerald-900">
        {mode === 'calibrate' ? t('floorPlan.calibrateHint') : t('floorPlan.drawHint')}
      </div>

      {/* Image canvas */}
      <div ref={containerRef} className="flex-1 relative overflow-auto bg-slate-100 flex items-center justify-center p-2">
        <div
          className="relative inline-block max-w-full"
          style={{ cursor: 'crosshair' }}
          onClick={handleCanvasClick}
        >
          <img
            ref={imgRef}
            src={floorPlan?.image_url ? (floorPlan.image_url.startsWith('http')
              ? floorPlan.image_url
              : `${process.env.REACT_APP_BACKEND_URL}${floorPlan.image_url}`) : ''}
            alt={floorPlan?.name || 'Floor plan'}
            onLoad={onImageLoad}
            draggable={false}
            className="max-w-full max-h-[75vh] block select-none"
            style={{ userSelect: 'none' }}
          />

          {/* SVG overlay — sized to displayed image */}
          {imgSize.w > 0 && imgSize.h > 0 && (
            <svg
              className="absolute top-0 left-0 pointer-events-none"
              width={imgSize.w}
              height={imgSize.h}
              viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
            >
              {/* Calibration line */}
              {mode === 'calibrate' && cssCalib.length === 2 && (
                <line
                  x1={cssCalib[0].x} y1={cssCalib[0].y}
                  x2={cssCalib[1].x} y2={cssCalib[1].y}
                  stroke="#0ea5e9" strokeWidth="2" strokeDasharray="4 4"
                />
              )}
              {mode === 'calibrate' && cssCalib.map((p, i) => (
                <circle key={`c-${i}`} cx={p.x} cy={p.y} r="6" fill="#0ea5e9" stroke="#fff" strokeWidth="2" />
              ))}

              {/* Drawn polyline */}
              {cssWaypoints.length >= 2 && (
                <polyline
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={cssWaypoints.map((p) => `${p.x},${p.y}`).join(' ')}
                />
              )}
              {isCircuit && cssWaypoints.length >= 2 && (
                <line
                  x1={cssWaypoints[cssWaypoints.length - 1].x}
                  y1={cssWaypoints[cssWaypoints.length - 1].y}
                  x2={cssWaypoints[0].x}
                  y2={cssWaypoints[0].y}
                  stroke={color} strokeWidth="2" strokeDasharray="6 4"
                />
              )}

              {/* Waypoint dots */}
              {mode === 'draw' && cssWaypoints.map((p, i) => (
                <circle key={`w-${i}`} cx={p.x} cy={p.y} r="4" fill={color} stroke="#fff" strokeWidth="2" />
              ))}

              {/* Start / end markers */}
              {cssWaypoints.length >= 1 && (
                <g>
                  <circle cx={cssWaypoints[0].x} cy={cssWaypoints[0].y} r="10" fill="#22c55e" stroke="#15803d" strokeWidth="2" />
                  <text x={cssWaypoints[0].x} y={cssWaypoints[0].y + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="bold">S</text>
                </g>
              )}
              {!isCircuit && cssWaypoints.length >= 2 && (
                <g>
                  <circle
                    cx={cssWaypoints[cssWaypoints.length - 1].x}
                    cy={cssWaypoints[cssWaypoints.length - 1].y}
                    r="10" fill="#dc2626" stroke="#7f1d1d" strokeWidth="2"
                  />
                  <text
                    x={cssWaypoints[cssWaypoints.length - 1].x}
                    y={cssWaypoints[cssWaypoints.length - 1].y + 4}
                    textAnchor="middle" fontSize="11" fill="#fff" fontWeight="bold"
                  >F</text>
                </g>
              )}
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}
